/**
 * A proposal's priority rating on its approval card: what the proposer suggested, what the CEO
 * approved, and — for the CEO alone — the control to change it.
 *
 * ⚠️ Only the CEO officeholder gets the control (`viewer.canSetPriority`, resolved by the proxy):
 * not the other approvers, not a delegate, and not an admin, who may record the CEO's APPROVAL on
 * his behalf but never his rating. Hiding the select is courtesy — the proxy refuses anyone else
 * (`roleApprovalPriority.priorityRefusal`).
 *
 * The draft is held by the CARD, not here, because the CEO's Approve button sends it: approving the
 * role approves the rating on screen, so confirming the suggestion is never a separate click and
 * picking another one is an override on the same click. "Save priority" is only for changing it
 * without approving, or after.
 */
import { useId } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { PRIORITY_RATINGS, type PriorityRating, type ProposalPriority } from '../../types/roleApprovals';
import { RatingBadge } from '../taCockpit/rolePrioritisation/prioritisationUi';
import { RADIUS } from './approvalUi';

interface Props {
  priority: ProposalPriority;
  /** What the select shows: the CEO's pending pick, else the approved rating, else the suggestion. */
  draft: PriorityRating | null;
  onDraftChange: (rating: PriorityRating | null) => void;
  /** The viewer is the CEO and the proposal is still open. */
  editable: boolean;
  /** Whether the CEO has already approved the role — decides what the hint beside the select says. */
  ceoApproved: boolean;
  ownerName: string;
  busy: boolean;
  onSave: (rating: PriorityRating | null) => void;
}

function Rated({ label, rating, empty }: { label: string; rating: PriorityRating | null; empty: string }) {
  return (
    <span className="flex items-center gap-1.5 text-xs">
      <span className="text-zinc-400">{label}</span>
      {rating ? <RatingBadge rating={rating} /> : <span className="text-zinc-500">{empty}</span>}
    </span>
  );
}

export function PriorityPanel({ priority, draft, onDraftChange, editable, ceoApproved, ownerName, busy, onSave }: Props) {
  const dirty = draft !== priority.approved;
  // One card per proposal on the page, so the label/select pair needs an id of its own.
  const selectId = useId();
  return (
    <div className={`${RADIUS} mb-3 flex flex-col gap-2 border border-zinc-200 px-3 py-2`}>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
        <Rated label="Suggested priority" rating={priority.suggested} empty="none" />
        <Rated label="Approved priority" rating={priority.approved} empty={`awaiting ${ownerName}`} />
        {priority.approvedByName && <span className="text-xs text-zinc-500">by {priority.approvedByName}</span>}
      </div>

      {editable && (
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor={selectId} className="text-xs font-medium text-zinc-700">
            Your priority
          </label>
          <select
            id={selectId}
            className="rounded border border-zinc-300 bg-white px-2 py-1 text-sm text-zinc-900 focus:border-trustedBlue focus:outline-none"
            value={draft ?? ''}
            onChange={(e) => onDraftChange((e.target.value || null) as PriorityRating | null)}
            disabled={busy}
          >
            <option value="">—</option>
            {PRIORITY_RATINGS.map((rating) => (
              <option key={rating} value={rating}>
                {rating}
                {rating === priority.suggested ? ' (suggested)' : ''}
              </option>
            ))}
          </select>
          {dirty && (
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => onSave(draft)}>
              Save priority
            </Button>
          )}
          <span className="text-xs text-zinc-500">
            {ceoApproved
              ? 'A change is posted in the request’s Slack thread.'
              : 'Approving the role approves this priority. A change from the suggestion is posted in Slack.'}
          </span>
        </div>
      )}
    </div>
  );
}
